import { useState } from "react";
import { motion } from "framer-motion";
import { 
  ClipboardList, 
  LogIn, 
  LogOut, 
  Calendar, 
  Search, 
  Phone, 
  Mail, 
  MapPin,
  Check,
  ChevronRight,
  Menu,
  X,
  Calculator,
  Instagram
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import InstagramPopup from "../components/InstagramPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "About", href: "#about" },
    { name: "Pricing", href: "#pricing" },
    { name: "Testimonials", href: "#testimonials" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800"
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <a href="#" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
              alt="AIP Logo" 
              className="h-20 w-auto"
            />
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href}
                className="nav-link text-sm font-medium"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <Link to="/login">
              <Button 
                variant="outline"
                className="btn-secondary rounded-full px-6 border-[#B8960B] text-[#B8960B] hover:bg-[#B8960B]/10"
                data-testid="nav-login-button"
              >
                Login
              </Button>
            </Link>
            <Button 
              className="btn-primary rounded-full px-6"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-cta-button"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-neutral-400 hover:text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="md:hidden py-4 border-t border-neutral-800"
          >
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href}
                className="block py-3 text-neutral-400 hover:text-white"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <Link to="/login" onClick={() => setIsOpen(false)}>
              <Button 
                variant="outline"
                className="btn-secondary rounded-full w-full mt-4 border-[#B8960B] text-[#B8960B]"
              >
                Login
              </Button>
            </Link>
            <Button 
              className="btn-primary rounded-full w-full mt-2"
              onClick={() => {
                setIsOpen(false);
                document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Get Quote
            </Button>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1727672951734-2406581ebb39?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-6 block">
            Professional Property Services
          </span>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            Trusted Property
            <br />
            <span className="text-[#B8960B]">Inventory</span> Experts
          </h1>
          <p className="text-base md:text-lg text-neutral-400 leading-relaxed max-w-2xl mx-auto mb-10">
            Comprehensive inventory reports, inspections, and snagging services. 
            Protecting landlords, agents, and tenants across the UK since day one.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button 
                className="btn-primary rounded-full px-8 py-6 text-lg font-semibold shadow-[0_0_20px_rgba(184,150,11,0.3)]"
                data-testid="hero-cta-primary"
              >
                Get Instant Quote <Calculator className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/book">
              <Button 
                variant="outline"
                className="btn-secondary rounded-full px-8 py-6 text-lg font-medium text-neutral-300"
                data-testid="hero-cta-secondary"
              >
                Book Now <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-neutral-600 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#B8960B] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Services Section with Bento Grid
const ServicesSection = () => {
  const services = [
    {
      icon: ClipboardList,
      title: "Inventory Reports",
      description: "Detailed photographic documentation of property condition, fixtures, and fittings before tenancy begins.",
      span: "col-span-1 md:col-span-2 row-span-2",
      image: "https://images.unsplash.com/photo-1680416124536-22b16c411402?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: LogIn,
      title: "Check-In",
      description: "Professional tenant check-in with comprehensive schedule of condition.",
      span: "col-span-1",
      image: "https://images.unsplash.com/photo-1604445415362-2a9840bd5ff6?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: LogOut,
      title: "Check-Out",
      description: "Thorough check-out inspections comparing against original inventory.",
      span: "col-span-1",
      image: "https://images.unsplash.com/photo-1562770584-eaf50b017307?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: Calendar,
      title: "Mid-Term Inspections",
      description: "Regular property inspections during tenancy to ensure compliance and maintenance.",
      span: "col-span-1 md:col-span-2",
      image: "https://images.unsplash.com/photo-1662454419716-c4c504728811?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: Search,
      title: "Snagging",
      description: "New build snagging surveys identifying defects before handover completion.",
      span: "col-span-1 md:col-span-2",
      image: "https://images.unsplash.com/photo-1680416124510-5eae1beca412?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
            What We Offer
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Our Services
          </h2>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto">
            Complete property documentation solutions tailored to your needs
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`bento-item rounded-2xl overflow-hidden ${service.span}`}
              data-testid={`service-card-${index}`}
            >
              <div className="p-6 h-full flex flex-col">
                <service.icon className="text-[#B8960B] mb-4" size={index === 0 ? 40 : 32} />
                <h3 className={`${index === 0 ? 'text-2xl md:text-3xl' : 'text-xl'} font-medium mb-2`}>{service.title}</h3>
                <p className="text-neutral-400 text-sm flex-grow">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const stats = [
    { value: "10K+", label: "Reports Completed" },
    { value: "500+", label: "Happy Clients" },
    { value: "15+", label: "Years Experience" },
    { value: "24h", label: "Report Delivery" }
  ];

  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-neutral-950/50" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
              About AIP
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Your Property,{" "}
              <span className="text-[#B8960B]">Our Priority</span>
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-6">
              AIP has been at the forefront of property inventory services, providing accurate, 
              detailed, and legally compliant documentation that protects all parties in the 
              rental process.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-8">
              Our team of AIIC certified professionals uses the latest technology to deliver 
              comprehensive reports that stand up to scrutiny in any deposit dispute.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center md:text-left">
                  <div className="text-3xl md:text-4xl font-bold text-[#B8960B] mb-1">{stat.value}</div>
                  <div className="text-sm text-neutral-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Property inspection"
                className="w-full h-auto object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const plans = [
    {
      name: "Studio/1 Bed",
      price: "85",
      features: [
        "Full inventory report",
        "Check-in service",
        "Check-out service",
        "Digital report delivery",
        "Photo documentation",
        "24h turnaround"
      ],
      highlighted: false
    },
    {
      name: "2 Bed",
      price: "95",
      features: [
        "Full inventory report",
        "Check-in service",
        "Check-out service",
        "Digital report delivery",
        "Photo documentation",
        "Priority support",
        "24h turnaround"
      ],
      highlighted: true
    },
    {
      name: "3+ Bed",
      price: "105",
      features: [
        "Full inventory report",
        "Check-in service",
        "Check-out service",
        "Digital report delivery",
        "Photo documentation",
        "Priority support",
        "24h turnaround"
      ],
      highlighted: false
    }
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
            Pricing
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Transparent Pricing
          </h2>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto">
            Competitive rates for professional inventory services
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`rounded-2xl p-8 ${
                plan.highlighted 
                  ? 'pricing-highlight bg-neutral-900/80' 
                  : 'bento-item'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.highlighted && (
                <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B] mb-4 block">
                  Most Popular
                </span>
              )}
              <h3 className="text-xl font-medium mb-2">{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">£{plan.price}</span>
                <span className="text-neutral-500 ml-2">/ property</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center gap-3 text-neutral-400">
                    <Check className="text-[#B8960B] flex-shrink-0" size={18} />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className={`w-full rounded-full py-5 ${
                  plan.highlighted 
                    ? 'btn-primary' 
                    : 'btn-secondary text-neutral-300'
                }`}
                onClick={() => window.location.href = '/quote'}
                data-testid={`pricing-cta-${index}`}
              >
                Get Quote
              </Button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "AIP has been our go-to for inventory services. Their reports are thorough, professional, and always delivered on time. Highly recommend!",
      author: "Sarah Mitchell",
      role: "Property Manager",
      image: "https://images.unsplash.com/photo-1629507208649-70919ca33793?crop=entropy&cs=srgb&fm=jpg&q=85&w=100"
    },
    {
      quote: "The snagging report saved us thousands. They caught defects we would have missed. Fantastic attention to detail.",
      author: "James Roberts",
      role: "Homeowner",
      image: "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&q=85&w=100"
    },
    {
      quote: "As a letting agent, having reliable inventory reports is crucial. AIP delivers consistently excellent service every time.",
      author: "Emma Thompson",
      role: "Letting Agent",
      image: "https://images.unsplash.com/photo-1629507313712-f21468afdf2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=100"
    }
  ];

  return (
    <section id="testimonials" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-neutral-950/50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            What Our Clients Say
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="testimonial-card rounded-2xl p-8"
              data-testid={`testimonial-card-${index}`}
            >
              <p className="text-neutral-300 leading-relaxed mb-6">"{testimonial.quote}"</p>
              <div>
                <div className="font-medium">{testimonial.author}</div>
                <div className="text-sm text-neutral-500">{testimonial.role}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Meet Our Team Section
const TeamSection = () => {
  const team = [
    {
      name: "Alison McAleavey",
      role: "Director",
      image: "https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/2ke425e8_F8CBC127-9E2E-4DAD-84AD-9F09FE5C5BEB.PNG",
      bio: "Since 2006, Alison has specialised in off-plan property investment, advising domestic and international investors on market trends, growth potential, and returns. She founded AIP to deliver independent, detailed inventory reports that protect landlords, agents, and property managers while reducing deposit disputes."
    },
    {
      name: "Jake McAleavey",
      role: "Inventory Clerk",
      image: null,
      bio: "With 7 years of experience as a professional inventory clerk, Jake delivers thorough and reliable property reports. Holding relevant industry qualifications, he ensures every inspection meets the highest standards of accuracy and professionalism."
    }
  ];

  return (
    <section id="team" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="team-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
            Our Team
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Meet Our Team
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {team.map((member, index) => (
            <div 
              key={index}
              className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 flex flex-col items-center text-center"
            >
              {member.image ? (
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-40 h-40 rounded-full object-cover object-top border-4 border-[#B8960B]/30 mb-6"
                />
              ) : (
                <div className="w-40 h-40 rounded-full bg-neutral-800 border-4 border-[#B8960B]/30 mb-6 flex items-center justify-center">
                  <span className="text-4xl font-bold text-[#B8960B]">{member.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
              )}
              <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
              <p className="text-[#B8960B] font-medium mb-4">{member.role}</p>
              <p className="text-neutral-400 leading-relaxed text-sm">
                {member.bio}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you! We'll be in touch shortly.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#B8960B]/80 mb-4 block">
              Contact Us
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Get In Touch
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-10">
              Ready to book an inventory or have questions? We're here to help. 
              Fill out the form and we'll get back to you within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <Phone className="text-[#B8960B]" size={20} />
                </div>
                <div>
                  <div className="text-sm text-neutral-500">Phone</div>
                  <div className="font-medium">+44 7841 238 061</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <Mail className="text-[#B8960B]" size={20} />
                </div>
                <div>
                  <div className="text-sm text-neutral-500">Email</div>
                  <div className="font-medium">inventories@aiplimited.co.uk</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <MapPin className="text-[#B8960B]" size={20} />
                </div>
                <div>
                  <div className="text-sm text-neutral-500">Location</div>
                  <div className="font-medium">Manchester, United Kingdom</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bento-item rounded-2xl p-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Name *</label>
                  <Input 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-950 border-neutral-800 focus:border-[#B8960B] rounded-lg h-12"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Email *</label>
                  <Input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-neutral-950 border-neutral-800 focus:border-[#B8960B] rounded-lg h-12"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Phone</label>
                  <Input 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-neutral-950 border-neutral-800 focus:border-[#B8960B] rounded-lg h-12"
                    placeholder="+44 123 456 7890"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Service</label>
                  <select 
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-neutral-950 border border-neutral-800 focus:border-[#B8960B] rounded-lg h-12 px-3 text-white"
                    data-testid="contact-service-select"
                  >
                    <option value="">Select a service</option>
                    <option value="inventory">Inventory Report</option>
                    <option value="check-in">Check-In</option>
                    <option value="check-out">Check-Out</option>
                    <option value="mid-term">Mid-Term Inspection</option>
                    <option value="snagging">Snagging</option>
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm text-neutral-400 mb-2">Message</label>
                <Textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="bg-neutral-950 border-neutral-800 focus:border-[#B8960B] rounded-lg resize-none"
                  placeholder="Tell us about your property..."
                  data-testid="contact-message-input"
                />
              </div>
              <Button 
                type="submit"
                className="btn-primary rounded-full w-full py-6 text-lg font-semibold"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Sending..." : "Send Enquiry"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-neutral-800" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center gap-8">
          {/* AIIC Accreditation Logo */}
          <a 
            href="https://theaiic.co.uk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
            data-testid="aiic-logo-link"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/d05n7unb_AIIC%20Black.jpeg" 
              alt="AIIC - Association of Independent Inventory Clerks" 
              className="h-16 w-auto rounded bg-white p-2"
            />
          </a>
          
          {/* AIP Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
            alt="AIP Logo" 
            className="h-12 w-auto"
          />
          
          {/* Navigation Links */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-500">
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-600">
            <Link to="/privacy" className="hover:text-neutral-400 transition-colors">Privacy Policy</Link>
            <Link to="/cookies" className="hover:text-neutral-400 transition-colors">Cookie Policy</Link>
            <Link to="/terms" className="hover:text-neutral-400 transition-colors">Terms & Conditions</Link>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://instagram.com/aiplimited"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow AIP on Instagram"
              data-testid="footer-instagram-link"
              className="group flex h-10 w-10 items-center justify-center rounded-full border border-neutral-800 bg-neutral-900/60 text-neutral-400 transition hover:border-[#B8960B]/60 hover:bg-gradient-to-br hover:from-[#B8960B] hover:via-pink-500 hover:to-purple-600 hover:text-white"
            >
              <Instagram size={18} />
            </a>
          </div>

          <div className="text-sm text-neutral-500">
            © 2026 AIP Limited. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <div className="section-divider" />
      <ServicesSection />
      <div className="section-divider" />
      <AboutSection />
      <div className="section-divider" />
      <PricingSection />
      <div className="section-divider" />
      <TestimonialsSection />
      <div className="section-divider" />
      <TeamSection />
      <div className="section-divider" />
      <ContactSection />
      <Footer />
      <InstagramPopup />
    </div>
  );
};

export default LandingPage;
